// The design lock for motion: how long each thing takes, in milliseconds,
// and how it eases. When the player asks the system for less motion, every
// wait is cut to `reducedCap` and the glides are skipped.

export type MotionConfig = {
  readonly reducedCap: number
  readonly ms: {
    readonly fadeOut: number
    readonly fadeIn: number
    readonly settle: number // other cards sliding to their new places
    readonly glide: number // a moved card flying to where it lands
    readonly glideStagger: number // between cards of a run
    readonly landSound: number // the bell rings as the first card lands
    readonly fold: number // three cards drawn into one
    readonly foldStagger: number
    readonly foldBirth: number // the new card blooming
    readonly foldShift: number
    readonly foldRest: number // a breath after a fold
    readonly flash: number
    readonly turnStagger: number
    readonly tremble: number // a refused lift
    readonly refused: number // rust rings on a refused rest
    readonly joinGlow: number // the ring that joined two cards
    readonly glimmer: number
    readonly ghost: number
    readonly snapBack: number
    readonly veilWait: number // before the win veil listens for a tap
    readonly titleDrift: number // the title sigil steps through the cube
    readonly confirm: number // a second tap must come this soon to confirm
  }
  readonly flipShare: number // each half of a turn-over takes this share of a glide
  readonly flipArc: number // px a turning card rises on its way
  readonly dragStart: number // px before a press becomes a drag
  readonly tilt: { readonly per: number; readonly max: number } // degrees per px dragged
  readonly easing: {
    readonly settle: string
    readonly glide: string
    readonly flip: string
    readonly fold: string
    readonly birth: string
    readonly flash: string
    readonly snap: string
  }
}

export const MOTION = {
  reducedCap: 60,
  ms: {
    fadeOut: 200,
    fadeIn: 240,
    settle: 220,
    glide: 300,
    glideStagger: 55,
    landSound: 170,
    fold: 520,
    foldStagger: 40,
    foldBirth: 620,
    foldShift: 260,
    foldRest: 380,
    flash: 900,
    turnStagger: 40,
    tremble: 320,
    refused: 950,
    joinGlow: 1500,
    glimmer: 2600,
    ghost: 1700,
    snapBack: 240,
    veilWait: 900,
    titleDrift: 1400,
    confirm: 4000,
  },
  flipShare: 0.55,
  flipArc: 20,
  dragStart: 6,
  tilt: { per: 0.01, max: 3 },
  easing: {
    settle: "cubic-bezier(.2,.7,.2,1)",
    glide: "cubic-bezier(.2,.75,.25,1)",
    flip: "cubic-bezier(.3,.6,.4,1)",
    fold: "cubic-bezier(.4,0,.2,1)",
    birth: "cubic-bezier(.2,.8,.2,1)",
    flash: "cubic-bezier(.1,.6,.3,1)",
    snap: "cubic-bezier(.3,.8,.3,1)",
  },
} as const satisfies MotionConfig
